import React from 'react';
import {useInView} from 'react-intersection-observer';
import styled from 'styled-components';
import {easing} from 'epic-ue-theme';
import {motion} from 'framer-motion';
import useMediaQuery from '@material-ui/core/useMediaQuery';

const Styled = styled(motion.div)`
    height: 100%;
    width: 100%;
`;

export interface AnimateOnViewWrapProps {
    threshold?: number;
    children?: React.ReactNode;
    initialInView?: boolean;
    triggerOnce?: boolean;
    translateY?: number;
    translateX?: number;
    scale?: number;
    startingOpacity?: number;
    duration?: number;
    delay?: number;
    fadeOut?: boolean;
    disable?: boolean;
}

/**
 * @param threshold Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
 * @param initialInView Set the initial value of the inView boolean. This can be used if you expect the element to be in the viewport to start with, and you want to trigger something when it leaves.
 * @param triggerOnce Only trigger the observer once.
 */
export const AnimateOnViewWrap = ({
    threshold,
    initialInView,
    children,
    translateY = 0,
    translateX = 0,
    scale = 1,
    triggerOnce = true,
    startingOpacity = 0,
    duration = 0.8,
    delay = 0,
    fadeOut = false,
    disable = false
}: AnimateOnViewWrapProps & React.CSSProperties): JSX.Element => {
    const {ref, inView} = useInView({
        threshold,
        initialInView,
        triggerOnce
    });

    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    if (disable || reducedMotion) {
        return <>{children}</>;
    }

    const variants = {
        animateInView: {
            opacity: fadeOut ? 0 : 1,
            translateY: 0,
            translateX: 0,
            scaleX: 1,
            scaleY: 1,
            transition: {
                delay,
                translateY: {duration: duration * 0.85},
                translateX: {duration: duration * 0.85},
                default: {
                    duration,
                    easing,
                    delay
                }
            }
        },
        animateOutOfView: {
            opacity: startingOpacity,
            translateY: translateY || 0,
            translateX: translateX || 0,
            scaleX: scale || '',
            scaleY: scale || ''
        }
    };

    return (
        <Styled
            ref={ref}
            className="anim-on"
            animate={inView ? 'animateInView' : 'animateOutOfView'}
            variants={variants}
            initial="animateOutOfView">
            {children}
        </Styled>
    );
};
